/**
 * ExpenditureLabelsContext
 * Provides soft labels for the 7 expenditure codes AND the system decimal places setting.
 * Labels are defined in System Admin > Environment Settings > Expenditure Code Names.
 * Decimal places are defined in System Admin > Environment Settings > Decimal Places.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const DEFAULTS = {
  exp_code_1: 'Account',
  exp_code_2: 'Restaurant',
  exp_code_3: 'Department Code',
  cost_centre: 'Cost Centre',
  exp_code_4: 'GL',
  exp_code_5: 'Property Code',
  exp_code_6: 'Nominal Code',
};

const ExpenditureLabelsContext = createContext({
  labels: DEFAULTS,
  decimalPlaces: 2,
  fmtVal: (v) => Number(v || 0).toFixed(2),
  fmtTotal: (v) => Number(v || 0).toFixed(2),
  loading: true,
  refresh: () => {},
});

export function ExpenditureLabelsProvider({ children }) {
  const [labels, setLabels] = useState(DEFAULTS);
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/expenditure-labels');
      if (res.data?.labels) setLabels(res.data.labels);
      if (res.data?.decimal_places != null) setDecimalPlaces(res.data.decimal_places);
    } catch {
      // Not authenticated yet or endpoint unavailable — use defaults
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const onAuth = () => setTimeout(refresh, 500);
    window.addEventListener('eyvo-auth-change', onAuth);
    return () => window.removeEventListener('eyvo-auth-change', onAuth);
  }, [refresh]);

  /** Format a value (price, qty, tax%, discount) using system decimal places with commas */
  /** Format a value for DISPLAY — with thousand commas */
  const fmtVal = useCallback((v) => {
    const num = Number(v || 0);
    return num.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
  }, [decimalPlaces]);

  /** Format a value for INPUT FIELDS — no commas, just decimal places */
  const fmtInput = useCallback((v) => Number(v || 0).toFixed(decimalPlaces), [decimalPlaces]);

  /** Format a total for DISPLAY — always 2dp with thousand commas */
  const fmtTotal = useCallback((v) => {
    const num = Number(v || 0);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  return (
    <ExpenditureLabelsContext.Provider value={{ labels, decimalPlaces, fmtVal, fmtInput, fmtTotal, loading, refresh }}>
      {children}
    </ExpenditureLabelsContext.Provider>
  );
}

export function useExpenditureLabels() {
  return useContext(ExpenditureLabelsContext);
}

/** Shortcut hook for just the formatting functions */
export function useDecimalFormat() {
  const { decimalPlaces, fmtVal, fmtInput, fmtTotal } = useContext(ExpenditureLabelsContext);
  return { decimalPlaces, fmtVal, fmtInput, fmtTotal };
}

export default ExpenditureLabelsContext;
